import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, Mail } from "lucide-react";
import { toast } from "sonner";

const TOPIC_OPTIONS = [
    { value: "certified_course", label: "Certified Course" },
    { value: "rope_belt_therapy_yoga", label: "Rope & Belt Therapy Yoga" },
    { value: "advance_asana", label: "Advance Asana" },
    { value: "yoga_meditation", label: "Yoga & Meditation" },
    { value: "health_workshop", label: "Health Workshop" },
    { value: "corporate_workshop", label: "Corporate Workshop" },
];

const MODE_OPTIONS = [
    { value: "online", label: "Online" },
    { value: "offline", label: "On Campus / Studio (Offline)" },
    { value: "home_tutions", label: "Home Tuitions" },
];

const empty = {
    first_name: "",
    last_name: "",
    email: "",
    topics: [],
    mode: "",
    message: "",
};

export default function WriteToUsSection({ embedded = false }) {
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);

    const valid =
        form.first_name.trim() &&
        form.last_name.trim() &&
        /\S+@\S+\.\S+/.test(form.email) &&
        form.topics.length > 0 &&
        form.mode &&
        form.message.trim();

    const toggleTopic = (value) => {
        setForm((f) => ({
            ...f,
            topics: f.topics.includes(value) ? f.topics.filter((t) => t !== value) : [...f.topics, value],
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!valid || submitting) return;
        setSubmitting(true);
        try {
            await api.post("/enquiries", form);
            toast.success("Enquiry sent", { description: "Our team will reach out to you shortly." });
            setForm(empty);
        } catch (err) {
            toast.error("Could not send enquiry", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const body = (
        <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold tracking-wide uppercase">
                    <Mail className="h-3.5 w-3.5" /> Have a question?
                </span>
                <h2 className="mt-5 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-ink-900">
                    Write to us
                </h2>
                <p className="mt-4 text-base md:text-lg text-ink-700 leading-relaxed max-w-2xl mx-auto">
                    Tell us what you're interested in — courses, workshops, or a tailored session at home. We'll reply with everything you need to know.
                </p>
            </div>

            <form
                onSubmit={onSubmit}
                className="bg-paper-alt rounded-3xl border border-gray-100 shadow-soft p-6 md:p-10 space-y-6"
                data-testid="write-to-us-form"
            >
                    {/* Name + email */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-sm">First name<span className="text-accent-600 ml-0.5">*</span></Label>
                            <Input
                                value={form.first_name}
                                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                                required
                                maxLength={80}
                                className="mt-1.5 rounded-xl"
                                data-testid="enquiry-first-name"
                            />
                        </div>
                        <div>
                            <Label className="text-sm">Last name<span className="text-accent-600 ml-0.5">*</span></Label>
                            <Input
                                value={form.last_name}
                                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                                required
                                maxLength={80}
                                className="mt-1.5 rounded-xl"
                                data-testid="enquiry-last-name"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm">Email<span className="text-accent-600 ml-0.5">*</span></Label>
                        <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                            required
                            className="mt-1.5 rounded-xl"
                            data-testid="enquiry-email"
                        />
                    </div>

                    {/* Topics (checkboxes) */}
                    <div>
                        <Label className="text-sm">
                            Enquire for?<span className="text-accent-600 ml-0.5">*</span>
                            <span className="ml-2 text-[11px] font-normal text-ink-400">(select all that apply)</span>
                        </Label>
                        <div className="mt-3 grid sm:grid-cols-2 gap-2.5" role="group" aria-labelledby="enquiry-topics">
                            {TOPIC_OPTIONS.map((t) => {
                                const checked = form.topics.includes(t.value);
                                return (
                                    <label
                                        key={t.value}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer transition ${checked ? "bg-brand-50 border-brand-300 text-ink-900" : "bg-white border-gray-200 hover:border-brand-200"}`}
                                        data-testid={`enquiry-topic-${t.value}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleTopic(t.value)}
                                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                            data-testid={`enquiry-topic-checkbox-${t.value}`}
                                        />
                                        <span className="text-sm font-medium">{t.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* Mode (radio) */}
                    <div>
                        <Label className="text-sm">
                            Are you looking for Online Mode or On Campus Mode (Offline)?<span className="text-accent-600 ml-0.5">*</span>
                        </Label>
                        <div className="mt-3 grid sm:grid-cols-3 gap-2.5" role="radiogroup">
                            {MODE_OPTIONS.map((m) => {
                                const selected = form.mode === m.value;
                                return (
                                    <label
                                        key={m.value}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer transition ${selected ? "bg-accent-50 border-accent-300 text-ink-900" : "bg-white border-gray-200 hover:border-accent-200"}`}
                                        data-testid={`enquiry-mode-${m.value}`}
                                    >
                                        <input
                                            type="radio"
                                            name="enquiry-mode"
                                            value={m.value}
                                            checked={selected}
                                            onChange={() => setForm((f) => ({ ...f, mode: m.value }))}
                                            className="h-4 w-4 text-accent-600 focus:ring-accent-500"
                                            data-testid={`enquiry-mode-radio-${m.value}`}
                                        />
                                        <span className="text-sm font-medium">{m.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* Message */}
                    <div>
                        <Label className="text-sm">Message<span className="text-accent-600 ml-0.5">*</span></Label>
                        <Textarea
                            value={form.message}
                            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                            required
                            rows={4}
                            maxLength={2000}
                            placeholder="Tell us a bit about your goals, schedule, or any questions you have..."
                            className="mt-1.5 rounded-xl"
                            data-testid="enquiry-message"
                        />
                    </div>

                    <div className="flex items-center justify-end">
                        <Button
                            type="submit"
                            disabled={!valid || submitting}
                            className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-7 py-5 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="enquiry-submit-btn"
                        >
                            <Send className="h-4 w-4 mr-1.5" />
                            {submitting ? "Sending…" : "Send enquiry"}
                        </Button>
                    </div>
                </form>
            </div>
    );

    if (embedded) {
        return <div data-testid="write-to-us-section-embedded">{body}</div>;
    }
    return (
        <section id="write-to-us" className="relative py-20 md:py-28 bg-white" data-testid="write-to-us-section">
            <div className="px-4 sm:px-6 lg:px-8">
                {body}
            </div>
        </section>
    );
}
